import { cloneElement, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { filterBy, orderBy } from "@progress/kendo-data-query";
import { MultiColumnComboBox } from "@progress/kendo-react-dropdowns";
import { FieldWrapper } from "@progress/kendo-react-form";
import { Label } from "@progress/kendo-react-labels";
import clsx from "clsx";

import { Icon, IconType } from "../../Icon/Icon";
import { SortableColumnHeader } from "../../MultiColumnComboBox/SortableColumnHeader";
import { getNextSort } from "../../MultiColumnComboBox/sorting";
import { ErrorTooltip } from "../ErrorTooltip";
import { useVirtualData } from "../hooks/useVirtualData";

import { buildFilterDescriptor } from "./helpers";
import * as styles from "./styles.module.scss";

import type { ReactElement } from "react";
import type { SortDescriptor } from "@progress/kendo-data-query";
import type {
  ComboBoxBlurEvent,
  ComboBoxChangeEvent,
  ComboBoxFilterChangeEvent,
  MultiColumnComboBoxColumn,
  MultiColumnComboBoxHandle,
  MultiColumnComboBoxProps,
} from "@progress/kendo-react-dropdowns";
import type { FieldRenderProps } from "@progress/kendo-react-form";
import type { ListItemProps } from "../../DropDownList/DropDownList";

export type OnChangeType = FieldRenderProps["onChange"];

interface MultiColumnComboBoxFieldProps<DataItem> extends Partial<FieldRenderProps> {
  className?: string;
  data: DataItem[];
  dataItemKey?: string;
  name: string;
  onChange: OnChangeType;
  optional?: boolean;
  columns: MultiColumnComboBoxProps["columns"];
  modified?: boolean;
  testId?: string;
  emptyMessage?: string;
  textField?: string;
  displayViewMap?: (item: DataItem) => string;
  filterable?: boolean;
  onSortChange?: (sort: SortDescriptor | null) => void;
  virtual?: {
    fetchPage: () => { total: number; data: DataItem[]; errors?: string[] };
    error?: string;
    errorText?: string;
    queryParams?: [];
  };
  controlConfig?: Omit<
    MultiColumnComboBoxProps,
    | "onChange"
    | "onBlur"
    | "onFocus"
    | "validationMessage"
    | "id"
    | "value"
    | "disabled"
    | "dataItemKey"
    | "textField"
    | "data"
    | "columns"
    | "filterable"
  >;
}

const headerSorting = (
  column: MultiColumnComboBoxColumn,
  sorting: SortDescriptor | undefined,
  onSort: (field: string) => void,
) => {
  if (typeof column.header !== "string" || !column.field) {
    return column.header;
  }

  return (
    <SortableColumnHeader
      className={styles.boxHeader}
      direction={sorting?.field === column.field ? sorting.dir : undefined}
      field={column.field}
      iconClassName={styles.boxHeaderIcon}
      inactiveIconClassName={styles.boxHeaderIcon_inactive}
      label={column.header}
      onSort={onSort}
      showInactiveIcon
      testId={`${column.field}-header`}
    />
  );
};

export const MultiColumnComboBoxField = <DataItem,>(
  props: MultiColumnComboBoxFieldProps<DataItem>,
) => {
  const inputRef = useRef<MultiColumnComboBoxHandle>(null);
  const [isFieldFocused, setIsFieldFocused] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  const [sortBy, setSortBy] = useState<SortDescriptor>();
  const [filter, setFilter] = useState("");

  const virtualData = useVirtualData({
    textField: props.textField,
    dataItemKey: props.dataItemKey,
    loadPage: props.virtual?.fetchPage,
    queryParams: props.virtual?.queryParams,
    defaultPageSize: 20,
    defaultSort: { field: props.columns[0].field, dir: "desc" },
  });

  const handleFilterChange = useCallback(
    (event: ComboBoxFilterChangeEvent) => {
      if (virtualData.enabled) {
        virtualData.onFilterChange(event);
        return;
      }

      setFilter(event.filter.value as string);

      if (props.controlConfig?.onFilterChange) {
        props.controlConfig.onFilterChange(event);
      }
    },
    [virtualData.enabled, virtualData.onFilterChange, props.controlConfig],
  );

  const hasError =
    props.modified && props.validationMessage?.length ? props.validationMessage : undefined;

  useEffect(() => {
    if (props.testId && inputRef.current?.element) {
      const inputEl = inputRef.current.element.querySelector("input");
      if (inputEl) {
        inputEl.setAttribute("data-testid", `${props.testId}-input`);
      }
    }
  }, [inputRef, props.testId]);

  const preparedData: DataItem[] = useMemo(() => {
    if (virtualData.enabled) {
      const sliced = virtualData.data.slice(
        virtualData.skip,
        (virtualData.skip || 0) + (virtualData.pageSize || 10),
      );

      return sliced;
    }

    let filteredData;

    if (props.filterable && filter) {
      const descriptor = buildFilterDescriptor(filter, props.columns);
      filteredData = filterBy(props.data, descriptor);
    } else {
      filteredData = props.data;
    }

    return sortBy?.dir ? orderBy(filteredData, [sortBy]) : filteredData;
  }, [
    virtualData.data,
    virtualData.enabled,
    virtualData.pageSize,
    filter,
    props.data,
    props.filterable,
    props.columns,
    sortBy,
  ]);

  const onFocus = () => {
    setShowTooltip(true);
    setIsFieldFocused(true);

    if (props.onFocus) {
      props.onFocus();
    }
  };

  const onBlur = () => {
    setShowTooltip(false);
    setIsFieldFocused(false);

    if (props.onBlur) {
      props.onBlur();
    }
  };

  const handleOpen = () => {
    if (virtualData.enabled) {
      virtualData.onPageChange({ page: { skip: 0, take: 20 } });
    }
  };

  const handleChange = (event: ComboBoxChangeEvent) => {
    props.onChange({ target: props.name, value: event.value });
  };

  const onIconLeave = () => {
    if (!isFieldFocused) {
      setShowTooltip(false);
    }
  };

  const listNoDataRender = () => {
    if (virtualData.enabled && virtualData.errorLoading) {
      return (
        <div className={styles.emptyMessage}>
          {props.virtual?.errorText ?? virtualData.errorLoading}
        </div>
      );
    }
    return <div className={styles.emptyMessage}>{props.emptyMessage ?? "No matches found"}</div>;
  };

  const sortByColumn = useCallback(
    (field: string) => {
      const nextSort = getNextSort(sortBy, field);
      setSortBy(nextSort);
      props.onSortChange?.(nextSort ?? null);

      if (virtualData.enabled) {
        virtualData.onSortChange(nextSort);
      }
    },
    [props.onSortChange, sortBy, virtualData.enabled, virtualData.onSortChange],
  );

  const preparedColumns = useMemo(
    () =>
      props.columns.map((column) => ({
        ...column,
        header: headerSorting(column, sortBy, sortByColumn),
      })),
    [props.columns, sortBy, sortByColumn],
  );

  const itemRender = useCallback(
    (li: ReactElement<HTMLLIElement>, itemProps: ListItemProps) => {
      const liProps = {
        ...li.props,
        "data-testid": "list-item",
      };

      if (virtualData.enabled && !itemProps.dataItem?.loaded) {
        const loadingItemChildren = (
          <span className={clsx("k-table-td", styles.popupRowLoading)} data-testid="td-row-loading">
            <span className="k-icon k-i-loading" />
          </span>
        );

        return cloneElement(li, { ...liProps }, loadingItemChildren);
      }

      const itemChildren = props.columns.map((column) => (
        <span
          className="k-table-td"
          data-testid={`td-${column.field}`}
          style={column.width ? { width: column.width } : undefined}
          key={column.field}
        >
          {itemProps.dataItem[column.field]}
        </span>
      ));

      return cloneElement(li, { ...liProps }, itemChildren);
    },
    [props.columns, virtualData.enabled],
  );

  const isEmpty = !props.value || (!!props.dataItemKey && !props.value[props.dataItemKey]);

  return (
    <FieldWrapper colSpan={props.colSpan} className={props.className ?? ""}>
      <div data-testid={props.testId ?? props.name}>
        {props.label && (
          <Label
            className={clsx(styles.label, props.required && isEmpty && styles.label_asterisks)}
            optional={props.optional}
            editorId={props.id}
            editorDisabled={props.disabled}
          >
            {props.label}
          </Label>
        )}
        <ErrorTooltip
          targetElement={inputRef.current?.element}
          error={hasError ? props.validationMessage : null}
          showTooltip={showTooltip}
          isFieldFocused={isFieldFocused}
          setShowTooltip={setShowTooltip}
        >
          <div
            data-testid={`${props.testId ?? props.name}-inner`}
            className={clsx(styles.border, !!hasError && isFieldFocused && styles.border_error)}
            title={props.label ?? props.name ?? "title for tooltips"}
          >
            <MultiColumnComboBox
              className={clsx(styles.select, !!hasError && styles.select_error)}
              data={preparedData}
              dataItemKey={props.dataItemKey}
              disabled={props.disabled}
              id={props.id}
              listNoDataRender={listNoDataRender}
              onBlur={onBlur}
              onChange={handleChange}
              onFocus={onFocus}
              ref={inputRef}
              textField={props.textField}
              columns={preparedColumns}
              valid={!hasError}
              value={props.value}
              filterable={props.filterable}
              onFilterChange={props.filterable ? handleFilterChange : undefined}
              onOpen={handleOpen}
              virtual={
                virtualData.enabled ?
                  {
                    skip: virtualData.skip,
                    pageSize: virtualData.pageSize,
                    total: virtualData.total,
                  }
                : undefined
              }
              loading={virtualData.enabled ? virtualData.loading : props.controlConfig?.loading}
              onPageChange={virtualData.onPageChange}
              itemRender={itemRender}
              {...props.controlConfig}
            />
            {!!hasError && (
              <Icon
                onMouseEnter={() => {
                  setShowTooltip(true);
                }}
                onMouseLeave={onIconLeave}
                className={styles.errorIcon}
                type={IconType.ErrorExclamation}
              />
            )}
          </div>
        </ErrorTooltip>
      </div>
    </FieldWrapper>
  );
};
